import ChatIcon from '@mui/icons-material/Chat';
import ForumIcon from '@mui/icons-material/Forum';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Skeleton, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { PageSection } from '@shared/components/PageSection';
import CustomTooltip from '@shared/components/Tooltip';
import { UserContext } from '@/shared/contexts/UserContext';
import { useThreads } from '@shared/hooks/useThreads';
import {
  ThreadContentFormat,
  ThreadSubjectType,
} from '@shared/models/Thread';
import {
  OnboardingAction,
  storeOnboardingIntent,
} from '@shared/utils/onboardingIntent';
import SignUpOrLogInModal from '../../../../authentication/components/SignUpOrLogInModal';
import CreateThreadForm from './CreateThreadForm';
import ThreadCard from './ThreadCard';

interface ThreadsPanelProps {
  companyId: number;
  companyName: string;
  highlightThreadId?: string;
  isTrialExpired?: boolean;
}

const Container = styled.div`
  min-height: 200px;
`;

const ThreadsList = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyState = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: linear-gradient(
    135deg,
    rgba(11, 116, 218, 0.02) 0%,
    rgba(120, 187, 241, 0.03) 100%
  );
  border-radius: 16px;
  border: 2px dashed rgba(11, 116, 218, 0.15);
`;

const EmptyIconWrapper = styled(Box)`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(11, 116, 218, 0.1) 0%,
    rgba(120, 187, 241, 0.15) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const EmptyTitle = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
  margin-bottom: 8px;
`;

const EmptyDescription = styled(Typography)`
  font-size: 0.9375rem;
  color: rgba(0, 0, 0, 0.5);
  max-width: 400px;
  line-height: 1.6;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LoginPrompt = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: linear-gradient(
    135deg,
    rgba(11, 116, 218, 0.04) 0%,
    rgba(120, 187, 241, 0.06) 100%
  );
  border-radius: 16px;
  border: 1px solid rgba(11, 116, 218, 0.12);
`;

const LoginButton = styled.button`
  margin-top: 20px;
  padding: 12px 28px;
  background: linear-gradient(135deg, #0b74da 0%, #2952a3 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(11, 116, 218, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(11, 116, 218, 0.4);
  }
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(
    90deg,
    rgba(11, 116, 218, 0.04) 0%,
    rgba(11, 116, 218, 0.02) 100%
  );
  border-radius: 12px;
  margin-bottom: 24px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatNumber = styled(Typography)`
  font-weight: 700;
  font-size: 1.25rem;
  color: #0b74da;
`;

const StatLabel = styled(Typography)`
  font-size: 0.875rem;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  padding: 16px 20px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  color: #dc2626;
  font-size: 0.9375rem;
  text-align: center;
`;

const GuidelinesHint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(
    135deg,
    rgba(11, 116, 218, 0.04) 0%,
    rgba(120, 187, 241, 0.06) 100%
  );
  border-radius: 10px;
  border: 1px solid rgba(11, 116, 218, 0.1);
  margin-bottom: 20px;
`;

const GuidelinesIcon = styled.div`
  color: #0b74da;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: 1px;
`;

const GuidelinesText = styled.div`
  font-size: 0.8125rem;
  color: #4b5563;
  line-height: 1.5;
`;

const GuidelinesLink = styled.span`
  color: #0b74da;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const GUIDELINES_CONTENT = (
  <div style={{ fontSize: '0.8125rem', lineHeight: 1.6 }}>
    <div style={{ fontWeight: 600, marginBottom: '8px' }}>Thread Guidelines:</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div>• Be respectful and professional</div>
      <div>• Share genuine experiences and insights</div>
      <div>• No personal attacks or harassment</div>
      <div>• No spam, self-promotion, or solicitation</div>
      <div>• Protect confidential information</div>
      <div>• Stay on topic and relevant to the company</div>
      <div>• Report inappropriate content</div>
    </div>
  </div>
);

const THREADS_SECTION_ID = 'threads-section';

const ThreadsPanel: React.FC<ThreadsPanelProps> = ({
  companyId,
  companyName,
  highlightThreadId,
  isTrialExpired = false,
}) => {
  const { user } = useContext(UserContext);
  const isAuthenticated = !!user;
  const [showAuthModal, setShowAuthModal] = useState(false);

  const {
    threads,
    loading,
    error,
    creating,
    createThread,
    createReply,
    updateThread,
    deleteThread,
  } = useThreads({
    autoFetch: !isTrialExpired,
    isAuthenticated,
    subjectId: companyId,
    subjectType: ThreadSubjectType.Company,
  });


  const handleCreateThread = async (
    content: string,
    isSlateFormat: boolean = false
  ) => {
    await createThread({
      content,
      contentFormat: isSlateFormat
        ? ThreadContentFormat.SlateJson
        : ThreadContentFormat.PlainText,
      subjectId: companyId,
      subjectType: ThreadSubjectType.Company,
    });
  };

  const handleReply = async (
    parentId: number,
    content: string,
    isSlateFormat: boolean = false
  ) => {
    await createReply(parentId, content, isSlateFormat);
  };

  const handleEdit = async (
    threadId: number,
    content: string,
    contentFormat: ThreadContentFormat
  ) => {
    await updateThread(threadId, content, contentFormat);
  };

  const handleDelete = async (threadId: number) => {
    await deleteThread(threadId);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
  };

  const totalThreads = threads.length;
  const totalReplies = threads.reduce((acc, thread) => {
    const countReplies = (t: typeof thread): number => {
      let count = t.children?.length || 0;
      t.children?.forEach((child) => {
        count += countReplies(child);
      });
      return count;
    };
    return acc + countReplies(thread);
  }, 0);

  const renderContent = () => {
    if (!isAuthenticated) {
      return (
        <LoginPrompt
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <EmptyIconWrapper>
            <ForumIcon sx={{ fontSize: 36, color: '#0b74da' }} />
          </EmptyIconWrapper>
          <EmptyTitle>Join the Conversation</EmptyTitle>
          <EmptyDescription>
            Sign in to participate in threads about {companyName}. Share
            your experiences, ask questions, and connect with other sales
            professionals.
          </EmptyDescription>
          <LoginButton onClick={() => {
            storeOnboardingIntent({
              action: OnboardingAction.JoinDiscussion,
              returnUrl: `${window.location.pathname}?scrollTo=${THREADS_SECTION_ID}`,
              companyId: String(companyId),
              companyName,
            });
            setShowAuthModal(true);
          }}>
            Sign In to Participate
          </LoginButton>
        </LoginPrompt>
      );
    }

    if (loading) {
      return (
        <LoadingContainer>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={140}
              sx={{ borderRadius: '16px' }}
            />
          ))}
        </LoadingContainer>
      );
    }

    if (error) {
      return <ErrorMessage>{error}</ErrorMessage>;
    }

    return (
      <>
        <GuidelinesHint>
          <GuidelinesIcon>
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </GuidelinesIcon>
          <GuidelinesText>
            Keep threads professional and constructive. Share genuine
            experiences to help others make informed decisions.{' '}
            <CustomTooltip
              title={GUIDELINES_CONTENT}
              placement="bottom-start"
              intent="info"
              maxWidth={320}
            >
              <GuidelinesLink>
                View full guidelines
              </GuidelinesLink>
            </CustomTooltip>
          </GuidelinesText>
        </GuidelinesHint>

        <CreateThreadForm
          onSubmit={handleCreateThread}
          isCreating={creating}
          companyName={companyName}
        />

        {threads.length > 0 && (
          <StatsBar>
            <StatItem>
              <ChatIcon sx={{ color: '#0b74da', fontSize: 22 }} />
              <StatNumber>{totalThreads}</StatNumber>
              <StatLabel>
                {totalThreads === 1 ? 'Thread' : 'Threads'}
              </StatLabel>
            </StatItem>
            {totalReplies > 0 && (
              <StatItem>
                <ForumIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                <StatNumber sx={{ color: '#6b7280', fontSize: '1rem' }}>
                  {totalReplies}
                </StatNumber>
                <StatLabel>
                  {totalReplies === 1 ? 'Reply' : 'Replies'}
                </StatLabel>
              </StatItem>
            )}
          </StatsBar>
        )}

        <AnimatePresence mode="popLayout">
          {threads.length === 0 ? (
            <EmptyState
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <EmptyIconWrapper>
                <ForumIcon sx={{ fontSize: 36, color: '#0b74da' }} />
              </EmptyIconWrapper>
              <EmptyTitle>No Threads Yet</EmptyTitle>
              <EmptyDescription>
                Be the first to start a thread about {companyName}. Share
                insights, ask questions, or discuss your experiences.
              </EmptyDescription>
            </EmptyState>
          ) : (
            <ThreadsList
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, staggerChildren: 0.1 }}
            >
              {threads.map((thread) => (
                <ThreadCard
                  key={thread.id}
                  thread={thread}
                  onReply={handleReply}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isCreating={creating}
                  highlightThreadId={highlightThreadId}
                />
              ))}
            </ThreadsList>
          )}
        </AnimatePresence>
      </>
    );
  };

  // Always expand the threads section (but individual thread replies start collapsed)
  const shouldExpandByDefault = true;

  return (
    <>
      <PageSection
        title={`${companyName} Threads`}
        collapsible={true}
        defaultExpanded={shouldExpandByDefault}
      >
        <Container id={THREADS_SECTION_ID}>{renderContent()}</Container>
      </PageSection>

      <SignUpOrLogInModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        contextMessage={`Sign in to join threads about ${companyName}`}
        handleAuthSuccess={handleAuthSuccess}
        initialMode="signup"
      />
    </>
  );
};

export default ThreadsPanel;
